import { CancelFunc, emptyCancel } from 'lib/cancel'
import { Status } from 'lib/rpc'
import { ActionStatus, Collector, expandValue, expandValueList, unaryInvoke } from 'lib/rpcts'
import { App } from 'pbts/c1/api/app/v1/app'
import { Role } from 'pbts/c1/api/iam/v1/iam'
import {
    IntrospectRequest,
    IntrospectResponse,
    User,
    UserServiceGetRequest,
    UserServiceGetResponse,
    UserServiceListResponse,
    UserView,
} from 'pbts/c1/api/user/v1/user'
import { UserServiceClient } from 'pbts/c1/api/user/v1/user.client'
import { Any } from 'pbts/google/protobuf/any'
import { Dispatch } from 'react'

export enum UserAction {
    getUser = 'UserAction.getUser',
    introspect = 'UserAction.introspect',
    setRoles = 'UserAction.setRoles',
}

export interface UserValue extends User {
    managers?: Array<User>
    roles?: Array<Role>
    delegatedUser?: User
    directories?: Array<App>
}

export interface UserViewResponse {
    userView?: UserView
    expanded: Any[]
}

export type GetUserAction = {
    type: UserAction.getUser
    payload: ActionStatus<UserValue>
}

export type IntrospectAction = {
    type: UserAction.introspect
    payload: ActionStatus<UserValue>
}

export type SetRolesAction = {
    type: UserAction.setRoles
    payload: ActionStatus<UserValue>
}
export type UserActions = GetUserAction | IntrospectAction | SetRolesAction

export const expandUserView = (msg: UserView | undefined, values: Any[]): UserValue => {
    const roles = expandValueList<Role>(msg?.rolesPath || '', Role, values)
    const managers = expandValueList<User>(msg?.managersPath || '', User, values)
    const delegatedUser = expandValue<User>(msg?.delegatedUserPath || '', User, values)
    const directories = expandValueList<App>(msg?.directoriesPath || '', App, values)
    const u: UserValue = User.create(msg?.user ?? {})
    u.roles = roles
    u.managers = managers
    u.delegatedUser = delegatedUser
    u.directories = directories
    return u
}

export const expandUserPayload = (payload: ActionStatus<UserViewResponse>): ActionStatus<UserValue> => {
    switch (payload.status) {
        case Status.OK:
            return {
                status: Status.OK,
                item: expandUserView(payload.item.userView, payload.item.expanded),
            }
        default:
            return payload
    }
}

export class UserCollector extends Collector<UserValue> {
    push(resp: UserServiceListResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): UserValue => {
                return expandUserView(v, resp.expanded)
            }),
        )
    }
}

export const getUser = (
    dispatch: Dispatch<UserActions>,
    userId: string,
    client?: UserServiceClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<UserServiceGetRequest, UserServiceGetResponse>(
        UserServiceGetRequest.create({
            id: userId,
            expandMask: { paths: ['*'] },
        }),
        client.get.bind(client),
        (payload) => {
            dispatch({
                type: UserAction.getUser,
                payload: expandUserPayload(payload),
            })
        },
    )
}

export const introspectUser = (
    dispatch: Dispatch<UserActions>,
    client?: UserServiceClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<IntrospectRequest, IntrospectResponse>(
        IntrospectRequest.create({ expandMask: { paths: ['*'] } }),
        client.introspect.bind(client),
        (payload) => {
            if (payload.status === Status.OK) {
                dispatch({
                    type: UserAction.introspect,
                    payload: { status: Status.OK, item: expandUserView(payload.item.userView, payload.item.expanded) },
                })
            } else {
                dispatch({
                    type: UserAction.introspect,
                    payload,
                })
            }
        },
    )
}
